// GalleryView.js
// Staff-only browse of all photographed pancakes across all shifts.
// Detail view: two-column on iPad/laptop (≥768px), single-column on mobile.
// Cutout is the default image; full photo is a toggle.

import { useState, useEffect, useRef } from "react";
import { db } from "./firebase";
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs } from "firebase/firestore";
import { getSeason, seasonOptions } from "./utils/season";
import HistoricalUploadModal from "./HistoricalUploadModal";
import GalleryEditPanel from "./GalleryEditPanel";
import ZoomableImage from "./components/ZoomableImage";
import { computeDisplayTitle } from "./utils/meditations";

// Theme — unified staff palette, imported from shared theme module.
import { STAFF_THEME, FONT } from "./theme/staff";
const BRAND = STAFF_THEME;

function fmtMs(ms) {
  if (ms == null || ms < 0) return null;
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function formatDate(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    + " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

const ORDER_TYPE_LABEL = { flash: "⚡ Flash", custom: "🎨 Custom", freestyle_flash: "⚡ Freestyle" };

export default function GalleryView({ navigate, embedded = false }) {
  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [isWide,   setIsWide]   = useState(() => window.innerWidth >= 768);

  // Controls
  const [sortOrder,    setSortOrder]    = useState("newest");
  const [activeTag,    setActiveTag]    = useState(null);
  const [searchQuery,  setSearchQuery]  = useState("");
  const [filterSeason, setFilterSeason] = useState("all");
  const [filterShift,  setFilterShift]  = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get("shift") || null;
  });

  // Detail — image toggle
  const [showFullPhoto, setShowFullPhoto] = useState(false);
  // Detail — in-app zoom state. When zoomed, EdgeNav hides to stop the
  // prev/next handles from eating two-finger gestures.
  const [detailZoomed, setDetailZoomed] = useState(false);
  useEffect(() => { setDetailZoomed(false); }, [selected?.id]); // reset on switch

  // Historical upload modal
  const [showHistoricalUpload, setShowHistoricalUpload] = useState(false);

  // Edit panel visibility on detail view — reset when leaving / switching selection
  const [editMode, setEditMode] = useState(false);
  useEffect(() => { setEditMode(false); }, [selected?.id]);

  // Reflection tags — local state so it updates immediately on tap
  const [reflectionTags, setReflectionTags] = useState([]);
  const [tagsSaved,      setTagsSaved]      = useState(false);
  useEffect(() => { setReflectionTags(selected?.artistTagsReflection ?? []); setTagsSaved(false); }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleReflectionTag(tagId) {
    const next = reflectionTags.includes(tagId) ? reflectionTags.filter(t => t !== tagId) : [...reflectionTags, tagId];
    setReflectionTags(next);
    setTagsSaved(false);
    try {
      await updateDoc(doc(db, "orders", selected.id), { artistTagsReflection: next });
      setTagsSaved(true);
      setTimeout(() => setTagsSaved(false), 1500);
    } catch (e) {
      console.error("Tag save failed:", e);
    }
  }

  // Load-more pagination for card list
  const [staffVisibleCount, setStaffVisibleCount] = useState(40);
  // Reset visible count when any filter changes
  useEffect(() => { setStaffVisibleCount(40); }, [sortOrder, activeTag, searchQuery, filterSeason, filterShift]);

  // Testimonial for the selected order
  const [testimonial, setTestimonial] = useState(null);
  useEffect(() => {
    if (!selected) { setTestimonial(null); return; }
    const q = query(collection(db, "testimonials"), where("orderId", "==", selected.id), where("status", "==", "published"));
    const unsub = onSnapshot(q, snap => {
      setTestimonial(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
    });
    return unsub;
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll position preservation
  const savedScrollY = useRef(0);

  // Swipe tracking
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => {
    const check = () => setIsWide(window.innerWidth >= 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // shiftId → eventName lookup (built from shift_summaries + order.eventName field)
  const [shiftNames, setShiftNames] = useState({});

  useEffect(() => {
    const q = query(collection(db, "orders"), where("state", "==", "done"));
    return onSnapshot(q, snap => {
      const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => o.photo_url && o.galleryPrivacy !== "private" && !o.hidden && !o.trashedAt);
      setOrders(loaded);
      setLoading(false);
      // Build shift name map from orders that already have eventName denormalized
      const fromOrders = {};
      loaded.forEach(o => { if (o.shiftId && o.eventName) fromOrders[o.shiftId] = o.eventName; });
      // Fill gaps from shift_summaries
      const missingIds = [...new Set(loaded.map(o => o.shiftId).filter(id => id && !fromOrders[id]))];
      if (missingIds.length > 0) {
        getDocs(collection(db, "shift_summaries")).then(ss => {
          const map = { ...fromOrders };
          ss.docs.forEach(d => { if (d.data().eventName) map[d.id] = d.data().eventName; });
          setShiftNames(map);
        }).catch(() => setShiftNames(fromOrders));
      } else {
        setShiftNames(fromOrders);
      }
    });
  }, []);

  function openDetail(order) {
    savedScrollY.current = window.scrollY;
    setSelected(order);
    setShowFullPhoto(!order.photo_cutout_url);
  }

  function closeDetail() {
    setSelected(null);
    requestAnimationFrame(() => window.scrollTo(0, savedScrollY.current));
  }

  function goTo(order) {
    setSelected(order);
    setShowFullPhoto(!order.photo_cutout_url);
  }

  function handleTouchStart(e) {
    // Ignore multi-touch — those belong to the ZoomableImage pinch gesture.
    if (e.touches.length > 1) { touchStartX.current = null; return; }
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }

  function handleTouchEnd(e, prevOrder, nextOrder) {
    if (editMode) return; // don't swipe-navigate during edit/touchup
    if (detailZoomed) return; // zoom-pan owns the gesture; don't navigate out
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0 && nextOrder) goTo(nextOrder);
    if (dx > 0 && prevOrder) goTo(prevOrder);
  }

  const allTags    = [...new Set(orders.flatMap(o => o.ai_tags ?? []))].sort();
  const allSeasons = seasonOptions(orders, o => o.photographedAt ?? o.createdAt);

  const displayed = orders
    .filter(o => {
      if (filterShift && o.shiftId !== filterShift) return false;
      if (filterSeason !== "all") {
        const s = getSeason(o.photographedAt ?? o.createdAt);
        if (s !== filterSeason) return false;
      }
      if (activeTag && !(o.ai_tags ?? []).includes(activeTag)) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const hit = s => (s ?? "").toLowerCase().includes(q);
        if (!hit(o.ai_description) && !(o.ai_tags ?? []).some(t => hit(t)) && !hit(o.subjectName) && !hit(o.recipientName) && !hit(o.customerName)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortOrder === "vibes") {
        const ra = (a.artistTags?.length ?? 0) + (a.artistTagsReflection?.length ?? 0);
        const rb = (b.artistTags?.length ?? 0) + (b.artistTagsReflection?.length ?? 0);
        if (rb !== ra) return rb - ra;
      }
      const ta = a.photographedAt?.toMillis?.() ?? 0;
      const tb = b.photographedAt?.toMillis?.() ?? 0;
      return sortOrder === "oldest" ? ta - tb : tb - ta;
    });

  // ── DETAIL VIEW ───────────────────────────────────────────────────────────
  // Resync selected to the live orders list so Firestore-side updates
  // (e.g., AI re-run rewriting ai_description) reflect without a refresh.
  const liveSelected = selected ? (orders.find(o => o.id === selected.id) || selected) : null;
  if (liveSelected) {
    const selected = liveSelected; // shadow the stale prop for the rest of this block
    const selectedIdx = displayed.findIndex(o => o.id === selected.id);
    const prevOrder   = selectedIdx > 0 ? displayed[selectedIdx - 1] : null;
    const nextOrder   = selectedIdx < displayed.length - 1 ? displayed[selectedIdx + 1] : null;

    const name       = computeDisplayTitle(selected) || selected.subjectName || selected.recipientName || selected.customerName;
    const hasCutout  = !!selected.photo_cutout_url;
    const imgSrc     = (!showFullPhoto && hasCutout) ? selected.photo_cutout_url : selected.photo_url;
    const dateStr    = formatDate(selected.photographedAt);
    const typeLabel  = ORDER_TYPE_LABEL[selected.orderType] ?? selected.orderType ?? "";
    const tags       = selected.ai_tags ?? [];

    // ── Shared sub-sections ──────────────────────────────────────────────
    const ImageBlock = (
      <div>
        <ZoomableImage
          src={imgSrc}
          alt={name}
          onZoomChange={setDetailZoomed}
          wrapperStyle={{
            width: "100%", aspectRatio: "1/1", borderRadius: "12px",
            background: hasCutout && !showFullPhoto ? BRAND.dark : "#000",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          imgStyle={{
            width: "100%", height: "100%",
            objectFit: hasCutout && !showFullPhoto ? "contain" : "cover",
            display: "block",
          }}
          filter={hasCutout && !showFullPhoto
            ? "drop-shadow(0 8px 28px rgba(0,0,0,0.8)) drop-shadow(0 2px 8px rgba(0,0,0,0.6))"
            : undefined}
        />
        {hasCutout && (
          <button onClick={() => setShowFullPhoto(v => !v)} style={{ marginTop: "0.6rem", background: "transparent", border: `1px solid ${BRAND.border}`, color: BRAND.muted, borderRadius: "8px", padding: "0.35rem 0.9rem", fontSize: "0.75rem", cursor: "pointer", width: "100%" }}>
            {showFullPhoto ? "Show Cutout" : "Show Full Photo"}
          </button>
        )}
      </div>
    );

    const shiftLabel = selected.eventName || shiftNames[selected.shiftId];
    const DiagnosticBlock = (
      <div style={{ borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.75rem", marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
        {shiftLabel   && <div style={{ color: BRAND.sky, fontSize: "0.78rem", fontWeight: "600" }}>📍 {shiftLabel}</div>}
        {dateStr      && <div style={{ color: `rgba(252,179,31,0.8)`, fontSize: "0.78rem" }}>📅 {dateStr}</div>}
        {typeLabel    && <div style={{ color: BRAND.dim, fontSize: "0.78rem" }}>{typeLabel}</div>}
        {selected.queueNumber && <div style={{ color: BRAND.dim, fontSize: "0.78rem" }}>Queue #{selected.queueNumber}</div>}
        {selected.artistName  && <div style={{ color: BRAND.cream, fontSize: "0.78rem" }}>🎨 {selected.artistName}</div>}
        {selected.notes && (
          <div style={{ color: BRAND.gold, fontSize: "0.82rem", fontStyle: "italic", marginTop: "0.2rem", background: "rgba(252,179,31,0.08)", borderLeft: `3px solid rgba(252,179,31,0.4)`, padding: "0.35rem 0.6rem", borderRadius: "0 6px 6px 0" }}>
            "{selected.notes}"
          </div>
        )}
        {(selected.drawingTimeMs != null || selected.cookTimeMs != null) && (
          <div style={{ display: "flex", gap: "1rem", marginTop: "0.3rem", flexWrap: "wrap" }}>
            {selected.drawingTimeMs != null && (
              <div style={{ color: `rgba(252,179,31,0.8)`, fontSize: "0.75rem" }}>✏️ draw {fmtMs(selected.drawingTimeMs)}</div>
            )}
            {selected.cookTimeMs != null && (
              <div style={{ color: `rgba(252,179,31,0.8)`, fontSize: "0.75rem" }}>🔥 cook {fmtMs(selected.cookTimeMs)}</div>
            )}
            {selected.drawingTimeMs != null && selected.cookTimeMs != null && (
              <div style={{ color: BRAND.gold, fontSize: "0.75rem" }}>⏱ total {fmtMs(selected.drawingTimeMs + selected.cookTimeMs)}</div>
            )}
          </div>
        )}
        {selected.catalogNumber && (
          <div style={{ color: BRAND.gold, fontSize: "0.75rem", marginTop: "0.3rem", fontWeight: 700 }}>
            Pancake #{selected.catalogNumber}
          </div>
        )}
        {selected.measurements && (
          <div style={{ display: "flex", gap: "1rem", marginTop: "0.3rem", flexWrap: "wrap" }}>
            <div style={{ color: `rgba(252,179,31,0.8)`, fontSize: "0.75rem" }}>📐 {selected.measurements.areaSqIn} sq in</div>
            <div style={{ color: `rgba(252,179,31,0.8)`, fontSize: "0.75rem" }}>📏 {selected.measurements.perimeterIn}" perimeter</div>
            <div style={{ color: `rgba(252,179,31,0.8)`, fontSize: "0.75rem" }}>⚖️ ~{selected.measurements.weightOz} oz</div>
          </div>
        )}
        {selected._isHistorical && selected.estimatedTotalTimeMs != null && (
          <div style={{ marginTop: "0.3rem" }}>
            <div style={{ color: BRAND.gold, fontSize: "0.75rem" }}>⏱ total ~{fmtMs(selected.estimatedTotalTimeMs)} (estimated)</div>
          </div>
        )}
        {selected._isHistorical && (
          <div style={{ marginTop: "0.4rem" }}>
            <span style={{ background: "rgba(252,179,31,0.12)", color: BRAND.gold, border: `1px solid rgba(252,179,31,0.4)`, borderRadius: "999px", padding: "0.15rem 0.55rem", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em" }}>🏛 ARCHIVE</span>
          </div>
        )}
      </div>
    );

    const RatingBlock = (
      <div style={{ borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.75rem", marginTop: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
          <div style={{ color: BRAND.dim, fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.07em" }}>Your Vibes</div>
          {tagsSaved && <div style={{ color: "#70be44", fontSize: "0.6rem" }}>✓ saved</div>}
        </div>
        {selected.artistTags?.length > 0 && (
          <div style={{ marginBottom: "0.6rem" }}>
            <div style={{ color: BRAND.dim, fontSize: "0.6rem", marginBottom: "0.3rem" }}>Gut (immediate)</div>
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {selected.artistTags.map(id => {
                const tag = ARTIST_TAGS.find(t => t.id === id);
                return tag ? <span key={id} style={{ fontSize: "1.1rem" }} title={tag.label}>{tag.emoji}</span> : null;
              })}
            </div>
          </div>
        )}
        <div>
          <div style={{ color: reflectionTags.length ? BRAND.muted : BRAND.dim, fontSize: "0.6rem", marginBottom: "0.3rem" }}>
            {reflectionTags.length ? "Reflection" : "How does it hold up? →"}
          </div>
          <TagStrip selected={reflectionTags} onToggle={toggleReflectionTag} size="1.3rem" showLabels={true} />
        </div>
      </div>
    );

    const DescriptionBlock = (
      <div style={{ borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.75rem", marginTop: "0.75rem" }}>
        <div style={{ color: BRAND.dim, fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "0.4rem", fontWeight: 700 }}>
          About this pancake
        </div>
        <p style={{ color: selected.ai_description ? BRAND.cream : BRAND.dim, fontSize: "0.88rem", lineHeight: 1.6, margin: "0 0 0.65rem", fontStyle: selected.ai_description ? "normal" : "italic" }}>
          {selected.ai_description ?? "No description yet"}
        </p>
        {tags.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
            {tags.map(t => (
              <span key={t} style={{ background: "rgba(74,164,225,0.12)", border: "1px solid rgba(74,164,225,0.35)", color: "#8ecff5", borderRadius: "20px", padding: "0.2rem 0.6rem", fontSize: "0.72rem" }}>{t}</span>
            ))}
          </div>
        )}
      </div>
    );

    // Meditation (read-only inline). Shows Dan the text he's already written
    // without needing to enter edit mode. Empty / not-yet-meditated pieces
    // get a compact prompt to write one.
    const hasMeditation = !!selected.meditation?.trim();
    const MeditationBlock = (
      <div style={{ borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.75rem", marginTop: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem", marginBottom: "0.4rem" }}>
          <span style={{ color: BRAND.gold, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>
            🥞 Dan's thoughts on this pancake
          </span>
          {hasMeditation && selected.meditationPinned && (
            <span style={{ background: "rgba(239,83,126,0.15)", color: BRAND.pink, border: `1px solid rgba(239,83,126,0.4)`, borderRadius: "999px", padding: "0.05rem 0.4rem", fontSize: "0.6rem", fontWeight: 700 }}>📌 pinned</span>
          )}
          {hasMeditation && selected.publicGallery && (
            <span style={{ background: "rgba(252,179,31,0.15)", color: BRAND.gold, border: `1px solid rgba(252,179,31,0.4)`, borderRadius: "999px", padding: "0.05rem 0.4rem", fontSize: "0.6rem", fontWeight: 700 }}>⭐ public</span>
          )}
        </div>
        {hasMeditation ? (
          <div>
            <p style={{ color: BRAND.cream, fontSize: "0.9rem", lineHeight: 1.6, margin: 0, fontStyle: "italic", whiteSpace: "pre-wrap" }}>
              {selected.meditation}
            </p>
            <button
              onClick={() => navigate(`/meditations?edit=${selected.id}`)}
              style={{ background: "none", border: "none", color: BRAND.dim, fontSize: "0.7rem", cursor: "pointer", padding: "0.3rem 0", marginTop: "0.3rem" }}
            >
              ✍️ Edit meditation
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div style={{ color: BRAND.dim, fontSize: "0.82rem", fontStyle: "italic" }}>
              No meditation written yet. {selected.meditationMark && <span style={{ color: BRAND.gold, fontStyle: "normal", fontWeight: 700 }}>✨ Marked</span>}
            </div>
            <button
              onClick={() => navigate(`/meditations?edit=${selected.id}`)}
              style={{ background: "rgba(252,179,31,0.15)", border: `1px solid rgba(252,179,31,0.35)`, color: BRAND.gold, borderRadius: "8px", padding: "0.3rem 0.7rem", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              ✍️ Write
            </button>
          </div>
        )}
      </div>
    );

    const TestimonialBlock = testimonial ? (
      <div style={{ borderTop: `1px solid ${BRAND.divider}`, paddingTop: "0.75rem", marginTop: "0.75rem" }}>
        <div style={{ color: BRAND.pink, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, marginBottom: "0.4rem" }}>
          💬 Guest Review
        </div>
        <p style={{ color: BRAND.cream, fontSize: "0.88rem", lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>
          "{testimonial.text}"
        </p>
        {testimonial.authorName && (
          <div style={{ color: BRAND.dim, fontSize: "0.78rem", marginTop: "0.35rem", fontWeight: 600 }}>
            — {testimonial.authorName}
          </div>
        )}
        {(shiftLabel || testimonial.context) && (
          <div style={{ color: BRAND.dim, fontSize: "0.68rem", marginTop: "0.15rem" }}>
            {shiftLabel || testimonial.context}
          </div>
        )}
      </div>
    ) : null;

    // ── Wide layout (desktop / iPad) ─────────────────────────────────────
    // Staff inspection view. Left = image (pinned), right = unified details
    // column (name, meditation, diagnostics, description, rating, edit). The
    // customer-facing preview lives at the 🔍 Preview button, not inline.
    if (isWide) return (
      <div
        style={{ position: "fixed", inset: 0, background: BRAND.dark, display: "flex", flexDirection: "column", fontFamily: FONT.body }}
        onTouchStart={handleTouchStart}
        onTouchEnd={e => handleTouchEnd(e, prevOrder, nextOrder)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.75rem 1.25rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
          <button onClick={() => { closeDetail(); }} style={hudBtn}>← Gallery</button>
          <div style={{ flex: 1, textAlign: "center", color: BRAND.muted, fontSize: "0.78rem" }}>
            {selectedIdx + 1} / {displayed.length}
          </div>
          <button onClick={() => window.open(`/preview-keepsake/${selected.id}`, "_blank")} style={hudBtn} title="Open the full customer keepsake in a new tab">
            🔍 Preview
          </button>
          <button onClick={() => setEditMode(v => !v)} style={{ ...hudBtn, background: editMode ? BRAND.gold : BRAND.raised, color: editMode ? BRAND.dark : "white", fontWeight: editMode ? 800 : 600 }}>
            {editMode ? "✓ Done" : "✏️ Edit"}
          </button>
        </div>
        <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
          {/* Left column — image, pinned (doesn't scroll with the details) */}
          <div style={{ width: "42%", padding: "1.75rem", overflowY: "auto", borderRight: `1px solid ${BRAND.divider}`, background: BRAND.dark, display: "flex", flexDirection: "column", gap: "0" }}>
            {ImageBlock}
          </div>
          {/* Right column — unified staff details */}
          <div style={{ flex: 1, padding: "1.75rem 2rem", overflowY: "auto" }}>
            {/* Name + context */}
            <div style={{ marginBottom: "0.4rem" }}>
              <div style={{ color: BRAND.pink, fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.2rem" }}>
                {selected.queueNumber ? `#${selected.queueNumber}` : "Pancake"}
              </div>
              <h2 style={{ color: "white", margin: 0, fontSize: "1.55rem", fontWeight: 700, lineHeight: 1.2 }}>{name}</h2>
              {selected.recipientName && selected.subjectName && selected.recipientName !== selected.subjectName && (
                <div style={{ color: BRAND.cream, fontSize: "0.85rem", marginTop: "0.2rem" }}>for {selected.recipientName}</div>
              )}
            </div>
            {RatingBlock}
            {MeditationBlock}
            {TestimonialBlock}
            {DiagnosticBlock}
            {DescriptionBlock}
            {editMode && (
              <GalleryEditPanel
                order={selected}
                onClose={() => setEditMode(false)}
                onDeleted={() => { setEditMode(false); closeDetail(); }}
              />
            )}
          </div>
          {/* Edge nav — hidden during edit/touchup to prevent accidental navigation */}
          {!editMode && !detailZoomed && prevOrder && <EdgeNav dir="left"  order={prevOrder} onClick={() => goTo(prevOrder)} />}
          {!editMode && !detailZoomed && nextOrder && <EdgeNav dir="right" order={nextOrder} onClick={() => goTo(nextOrder)} />}
        </div>
      </div>
    );

    // ── Narrow layout ────────────────────────────────────────────────────
    return (
      <div
        style={{ position: "fixed", inset: 0, background: BRAND.dark, display: "flex", flexDirection: "column", fontFamily: FONT.body }}
        onTouchStart={handleTouchStart}
        onTouchEnd={e => handleTouchEnd(e, prevOrder, nextOrder)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.6rem 0.85rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, flexShrink: 0 }}>
          <button onClick={() => { closeDetail(); }} style={hudBtn}>←</button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "white", fontWeight: "bold", fontSize: "0.92rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.queueNumber ? `#${selected.queueNumber} · ` : ""}{name}
            </div>
            <div style={{ color: BRAND.muted, fontSize: "0.72rem" }}>{selectedIdx + 1} / {displayed.length}</div>
          </div>
          <button onClick={() => window.open(`/preview-keepsake/${selected.id}`, "_blank")} style={{ ...hudBtn, flexShrink: 0 }} title="Preview customer keepsake">
            🔍
          </button>
          <button onClick={() => setEditMode(v => !v)} style={{ ...hudBtn, background: editMode ? BRAND.gold : BRAND.raised, color: editMode ? BRAND.dark : "white", fontWeight: editMode ? 800 : 600, flexShrink: 0 }}>
            {editMode ? "✓" : "✏️"}
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", position: "relative", background: BRAND.dark, padding: "1rem" }}>
          {ImageBlock}
          {RatingBlock}
          {MeditationBlock}
          {TestimonialBlock}
          {DiagnosticBlock}
          {DescriptionBlock}
          {editMode && (
            <GalleryEditPanel
              order={selected}
              onClose={() => setEditMode(false)}
              onDeleted={() => { setEditMode(false); closeDetail(); }}
            />
          )}
        </div>
        {!editMode && !detailZoomed && prevOrder && <EdgeNav dir="left"  order={prevOrder} onClick={() => goTo(prevOrder)} />}
        {!editMode && !detailZoomed && nextOrder && <EdgeNav dir="right" order={nextOrder} onClick={() => goTo(nextOrder)} />}
      </div>
    );
  }

  // ── CARD LIST ─────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: BRAND.dark, fontFamily: FONT.body, display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "0.75rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, gap: "0.5rem", flexWrap: "wrap" }}>
        <div style={{ color: BRAND.pink, fontWeight: "bold", fontSize: "1rem" }}>🖼 Gallery</div>
        <div style={{ color: BRAND.muted, fontSize: "0.75rem", flex: 1 }}>{loading ? "" : `${displayed.length} / ${orders.length}`}</div>
        <button onClick={() => setShowHistoricalUpload(true)} style={{ ...hudBtn, background: BRAND.gold, color: BRAND.dark, fontWeight: 800, whiteSpace: "nowrap" }}>🏛 Upload historical</button>
        {!embedded && <button onClick={() => navigate("/menu")} style={hudBtn}>← Menu</button>}
      </div>
      {showHistoricalUpload && (
        <HistoricalUploadModal onClose={() => setShowHistoricalUpload(false)} />
      )}
      <div style={{ padding: "0.6rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.divider}`, display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
        <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search names, descriptions, tags…"
          style={{ flex: 1, minWidth: "140px", background: BRAND.raised, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: "white", fontSize: "0.85rem", padding: "0.45rem 0.7rem", outline: "none" }} />
        {allSeasons.length > 1 && (
          <select value={filterSeason} onChange={e => setFilterSeason(e.target.value)}
            style={{ background: BRAND.raised, border: `1px solid ${BRAND.border}`, borderRadius: "8px", color: filterSeason !== "all" ? "white" : BRAND.muted, fontSize: "0.8rem", padding: "0.4rem 0.6rem", outline: "none", cursor: "pointer" }}>
            <option value="all">All Seasons</option>
            {allSeasons.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <button onClick={() => setSortOrder(s => s === "newest" ? "oldest" : s === "oldest" ? "vibes" : "newest")} style={{ ...hudBtn, whiteSpace: "nowrap", flexShrink: 0 }}>
          {sortOrder === "newest" ? "↓ Newest" : sortOrder === "oldest" ? "↑ Oldest" : "★ Top Rated"}
        </button>
      </div>
      {filterShift && (
        <div style={{ padding: "0.4rem 1rem", background: "rgba(74,164,225,0.08)", borderBottom: `1px solid ${BRAND.divider}`, display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
          <span style={{ color: BRAND.sky, fontSize: "0.78rem", fontWeight: 700 }}>
            📋 Showing shift: {shiftNames[filterShift] || filterShift.slice(0, 8)}
          </span>
          <button onClick={() => { setFilterShift(null); window.history.replaceState(null, "", "/library?tab=pancakes"); }} style={{ background: "rgba(255,255,255,0.1)", border: `1px solid ${BRAND.border}`, borderRadius: "6px", color: BRAND.muted, fontSize: "0.7rem", padding: "0.2rem 0.5rem", cursor: "pointer" }}>✕ Clear</button>
        </div>
      )}
      {allTags.length > 0 && (
        <div style={{ padding: "0.5rem 1rem", background: BRAND.panel, borderBottom: `1px solid ${BRAND.divider}`, display: "flex", gap: "0.4rem", overflowX: "auto", flexShrink: 0 }}>
          {allTags.map(tag => (
            <button key={tag} onClick={() => setActiveTag(t => t === tag ? null : tag)} style={{
              background: activeTag === tag ? BRAND.sky : "rgba(74,164,225,0.1)",
              color: activeTag === tag ? "white" : "#8ecff5",
              border: `1px solid ${activeTag === tag ? BRAND.sky : "rgba(74,164,225,0.3)"}`,
              borderRadius: "20px", padding: "0.35rem 0.8rem", fontSize: "0.75rem",
              fontWeight: activeTag === tag ? "bold" : "normal",
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}>{tag}</button>
          ))}
        </div>
      )}
      {loading ? (
        <div style={{ color: BRAND.muted, padding: "3rem", textAlign: "center" }}>Loading…</div>
      ) : displayed.length === 0 ? (
        <div style={{ color: BRAND.muted, padding: "3rem", textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🥞</div>
          {orders.length === 0 ? "No photos yet" : "No results"}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", padding: "1rem" }}>
          {displayed.slice(0, staffVisibleCount).map(order => <GalleryCard key={order.id} order={order} shiftNames={shiftNames} onTap={() => openDetail(order)} />)}
          {staffVisibleCount < displayed.length && (
            <button
              onClick={() => setStaffVisibleCount(c => Math.min(c + 40, displayed.length))}
              style={{
                background: BRAND.raised, border: `1px solid ${BRAND.border}`,
                borderRadius: "10px", padding: "0.75rem", cursor: "pointer",
                color: BRAND.gold, fontSize: "0.85rem", fontWeight: 700,
                textAlign: "center",
              }}
            >
              Load more ({displayed.length - staffVisibleCount} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const ARTIST_TAGS = [
  { id: "fire",  emoji: "🔥", label: "Fire" },
  { id: "clean", emoji: "🎯", label: "Clean" },
  { id: "magic", emoji: "✨", label: "Magic" },
  { id: "lab",   emoji: "🧪", label: "Lab" },
  { id: "grind", emoji: "💪", label: "Grind" },
  { id: "rough", emoji: "🫠", label: "Rough" },
  { id: "cute",  emoji: "🥰", label: "Cute" },
  { id: "funny", emoji: "😂", label: "Funny" },
];

function TagStrip({ selected, onToggle, size = "1.3rem", showLabels = false }) {
  return (
    <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
      {ARTIST_TAGS.map(tag => {
        const active = selected?.includes(tag.id);
        return (
          <button key={tag.id} onClick={() => onToggle?.(tag.id)} style={{
            background: active ? "rgba(252,179,31,0.18)" : "rgba(255,255,255,0.05)",
            border: active ? "2px solid rgba(252,179,31,0.6)" : "2px solid rgba(255,255,255,0.1)",
            borderRadius: "8px", padding: showLabels ? "0.25rem 0.5rem" : "0.25rem 0.35rem",
            cursor: onToggle ? "pointer" : "default",
            display: "flex", alignItems: "center", gap: "3px",
            transition: "all 0.15s ease",
            transform: active ? "scale(1.06)" : "scale(1)",
          }}>
            <span style={{ fontSize: size, lineHeight: 1 }}>{tag.emoji}</span>
            {showLabels && <span style={{ color: active ? "#fcb31f" : "rgba(255,255,255,0.4)", fontSize: "0.65rem", fontWeight: "bold" }}>{tag.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

// One-tap ⭐ overlay for Library cards. Toggles order.isShowcase, which drives
// the public /gallery "Showpieces" default view. Stops click propagation so it
// never opens the card's detail view. Optimistic local state for instant feel;
// onSnapshot reconciles.
function ShowcaseStar({ order }) {
  const [on, setOn] = useState(!!order.isShowcase);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOn(!!order.isShowcase); }, [order.isShowcase]);

  async function toggle(e) {
    e.stopPropagation();
    if (busy) return;
    const next = !on;
    setOn(next);
    setBusy(true);
    try {
      await updateDoc(doc(db, "orders", order.id), {
        isShowcase: next,
        showcasedAt: next ? new Date() : null,
      });
    } catch (err) {
      setOn(!next); // revert on failure
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={toggle}
      title={on ? "Showpiece — shown in public gallery" : "Mark as showpiece"}
      style={{
        position: "absolute", top: "5px", right: "5px",
        width: "32px", height: "32px", borderRadius: "50%",
        background: on ? "rgba(252,179,31,0.95)" : "rgba(6,12,32,0.6)",
        border: on ? "1px solid #fcb31f" : "1px solid rgba(255,255,255,0.25)",
        color: on ? "#1a1a1a" : "rgba(255,255,255,0.85)",
        fontSize: "1rem", lineHeight: 1, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: on ? "0 0 10px rgba(252,179,31,0.6)" : "none",
        transition: "all 0.15s ease",
        opacity: busy ? 0.6 : 1,
      }}
    >
      {on ? "★" : "☆"}
    </button>
  );
}

function GalleryCard({ order, shiftNames, onTap }) {
  const name      = computeDisplayTitle(order) || order.subjectName || order.recipientName || order.customerName;
  const tags      = order.ai_tags ?? [];
  const hasCutout = !!order.photo_cutout_url;
  const thumbSrc  = hasCutout
    ? (order.cutoutThumbnailUrl || order.photo_cutout_url)
    : (order.thumbnailUrl || order.photo_url);
  const vibes = [...(order.artistTags || []), ...(order.artistTagsReflection || [])].filter((v, i, a) => a.indexOf(v) === i);

  const dateStr = (() => {
    const ts = order.completedAt ?? order.photographedAt ?? order.createdAt;
    if (!ts) return null;
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  })();

  const shiftLabel = order.eventName || shiftNames?.[order.shiftId];
  const drawMs  = order.drawingTimeMs;
  const cookMs  = order.cookTimeMs;
  const totalMs = drawMs != null && cookMs != null ? drawMs + cookMs : null;

  return (
    <div onClick={onTap} style={{ background: BRAND.card, borderRadius: "12px", overflow: "hidden", cursor: "pointer", border: `1px solid ${BRAND.border}`, display: "flex", flexDirection: "row" }}>
      {/* Thumbnail — cutout floats on dark bg with drop shadow */}
      <div style={{ width: "140px", flexShrink: 0, position: "relative", aspectRatio: "1/1", background: hasCutout ? BRAND.dark : "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <img src={thumbSrc} alt={name} style={{
          width: "100%", height: "100%",
          objectFit: hasCutout ? "contain" : "cover",
          display: "block",
          filter: hasCutout
            ? "drop-shadow(0 4px 14px rgba(0,0,0,0.75)) drop-shadow(0 1px 4px rgba(0,0,0,0.5))"
            : "none",
        }} />
        {/* One-tap showpiece star — flags this pancake for the public /gallery
            "Showpieces" default view. Toggles order.isShowcase. */}
        <ShowcaseStar order={order} />
      </div>
      <div style={{ flex: 1, padding: "0.75rem 1rem", display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.3rem", minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
          {order.queueNumber && (
            <div style={{ background: BRAND.pink, color: "white", fontSize: "0.7rem", fontWeight: "bold", padding: "0.15rem 0.5rem", borderRadius: "6px" }}>#{order.queueNumber}</div>
          )}
          {dateStr && <div style={{ color: "rgba(252,179,31,0.75)", fontSize: "0.68rem" }}>{dateStr}</div>}
          {shiftLabel && <div style={{ color: "rgba(74,164,225,0.8)", fontSize: "0.68rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "120px" }}>📍 {shiftLabel}</div>}
        </div>
        <div style={{ color: "white", fontWeight: "bold", fontSize: "1rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
        {order.subjectName && <div style={{ color: BRAND.cream, fontSize: "0.8rem" }}>for {order.recipientName}</div>}
        {order.notes && (
          <div style={{ color: `rgba(252,179,31,0.85)`, fontSize: "0.72rem", fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>📝 {order.notes}</div>
        )}
        {order.ai_description ? (
          <div style={{ color: BRAND.muted, fontSize: "0.75rem", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{order.ai_description}</div>
        ) : (
          <div style={{ color: BRAND.dim, fontSize: "0.75rem", fontStyle: "italic" }}>No description yet</div>
        )}
        {(drawMs != null || totalMs != null) && (
          <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
            {drawMs  != null && <span style={{ color: "rgba(252,179,31,0.75)", fontSize: "0.68rem" }}>✏️ {fmtMs(drawMs)}</span>}
            {cookMs  != null && <span style={{ color: "rgba(252,179,31,0.75)", fontSize: "0.68rem" }}>🔥 {fmtMs(cookMs)}</span>}
            {totalMs != null && <span style={{ color: BRAND.gold, fontSize: "0.68rem" }}>⏱ {fmtMs(totalMs)}</span>}
          </div>
        )}
        {vibes.length > 0 && (
          <div style={{ display: "flex", gap: "2px" }}>
            {vibes.map(id => {
              const tag = ARTIST_TAGS.find(t => t.id === id);
              return tag ? <span key={id} style={{ fontSize: "1rem" }} title={tag.label}>{tag.emoji}</span> : null;
            })}
          </div>
        )}
        {tags.length > 0 && (
          <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", marginTop: "0.1rem" }}>
            {tags.slice(0, 4).map(t => <span key={t} style={{ background: "rgba(74,164,225,0.12)", color: "#8ecff5", border: "1px solid rgba(74,164,225,0.25)", borderRadius: "10px", padding: "0.1rem 0.45rem", fontSize: "0.65rem" }}>{t}</span>)}
            {tags.length > 4 && <span style={{ color: BRAND.dim, fontSize: "0.65rem", alignSelf: "center" }}>+{tags.length - 4}</span>}
          </div>
        )}
        <div style={{ color: BRAND.dim, fontSize: "0.55rem", fontFamily: "monospace", textAlign: "right", marginTop: "auto", opacity: 0.6 }}>
          GTS-{order.id.slice(-6).toUpperCase()}
        </div>
      </div>
    </div>
  );
}

// Translucent edge-tap nav button for detail view swipe navigation
function EdgeNav({ dir, order, onClick }) {
  const name = computeDisplayTitle(order) || order.subjectName || order.recipientName || order.customerName;
  const isLeft = dir === "left";
  return (
    <button
      onClick={onClick}
      style={{
        position: "absolute",
        top: "50%", transform: "translateY(-50%)",
        [isLeft ? "left" : "right"]: 0,
        background: "rgba(26,10,18,0.7)",
        border: "none",
        color: BRAND.muted,
        padding: "1.2rem 0.6rem",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "0.3rem",
        zIndex: 10,
        borderRadius: isLeft ? "0 10px 10px 0" : "10px 0 0 10px",
        backdropFilter: "blur(6px)",
        maxWidth: "54px",
      }}
    >
      <span style={{ fontSize: "1.2rem", lineHeight: 1 }}>{isLeft ? "‹" : "›"}</span>
      <span style={{ fontSize: "0.6rem", color: BRAND.dim, writingMode: "vertical-rl", textOrientation: "mixed", overflow: "hidden", maxHeight: "80px", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {name}
      </span>
    </button>
  );
}

const hudBtn = {
  background: "transparent", border: `1px solid rgba(74,164,225,0.35)`, color: "rgba(255,255,255,0.6)",
  borderRadius: "8px", padding: "0.4rem 0.75rem", fontSize: "0.8rem", cursor: "pointer",
};
